import { jsx as _jsx } from "react/jsx-runtime";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createContext, useContext, useState } from 'react';
import App from './App';
import { AiLoaderProvider } from './components/ui/loader';
import { ConfigProvider, ThemeProvider, ToastProvider } from './hoc';
// import { SessionProvider } from "next-auth/react";
// Set by the standalone app (index.dev.tsx) above the router, so every
// widget-backed route shares one client instead of each AiInterview mount
// creating (and losing, on route change) its own. Left null for the embedded
// widget bundle (index.tsx), where each widget is mounted as its own React
// root and should own its client.
export const SharedAiInterviewQueryClientContext = createContext(null);
export function createAiInterviewQueryClient() {
    return new QueryClient({
        defaultOptions: {
            queries: {
                // Do NOT refetch on window focus / network reconnect. With the bare
                // default (refetchOnWindowFocus: true) every query refetched each
                // time the browser tab regained focus, flipping `isFetching` and
                // making pages that gate their UI on it flash back to their loading
                // skeletons — the "page reloads when I switch browser tabs and come
                // back" seen across the app. Individual queries can still opt back in
                // when they need live refresh.
                refetchOnWindowFocus: false,
                refetchOnReconnect: false,
            },
        },
    });
}
export const AiInterview = ({ config, isPreview = false, theme, children, session, }) => {
    // Reuse an ancestor QueryClientProvider's client when one exists (the
    // standalone app provides a single client above the router — see
    // index.dev.tsx — so navigating between widget-backed routes keeps the
    // cache instead of wiping it on every mount/unmount). Only create a new
    // client when this is the outermost provider, i.e. the embedded widget
    // bundle (src/index.tsx), where each widget is its own React root anyway.
    const inheritedQueryClient = useContext(SharedAiInterviewQueryClientContext);
    const [ownQueryClient] = useState(createAiInterviewQueryClient);
    const queryClient = inheritedQueryClient !== null && inheritedQueryClient !== void 0 ? inheritedQueryClient : ownQueryClient;
    return (_jsx(QueryClientProvider, { client: queryClient, children: _jsx(ThemeProvider, { theme: theme, children: _jsx(ConfigProvider, { config: config, children: _jsx(ToastProvider, { children: _jsx(AiLoaderProvider, { children: _jsx(App, { isPreview: isPreview, children: children }) }) }) }) }) }));
};
